import React from 'react';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FilterConfigModalItem } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { ModalFiltersConfigSection } from '@rocco/ui/filter-sort/types/filter-config-section';
import { FiltersModalElement } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { RefineColorGrid } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineColorGrid';
import { RefinePillGroup } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefinePillGroup';
import { resolveSectionFilterState } from '@rocco/ui/filter-sort/components/filters-modal/refine/get-refine-presentation';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';

type UpdateFilterState = (
    filterKey: FilterStatesKey,
    newState: FilterState,
    shouldAggregate?: boolean,
) => void;

interface RefineDrillInViewProps {
    /** The sections rendered in this sub-view (one, or all More-filters sections). */
    sections: { section: ModalFiltersConfigSection; isFacet: boolean }[];
    /** Show each section's title above its controls (the More-filters view). */
    showSectionTitles?: boolean;
    filterStates: FilterStates;
    onUpdateFilterState: UpdateFilterState;
    onUpdateFacetFilterState: UpdateFilterState;
    onAlphabeticalGrouping: (args: AggregationArgs) => void;
}

/**
 * The body of a drill-in sub-view, operating on the same staged states as
 * the root view — the footer CTA keeps showing the live count. Enum filters
 * render as the refine pill group; ranges, searchable lists and everything
 * else go through the shared element switchboard.
 */
export const RefineDrillInView = (props: RefineDrillInViewProps) => {
    const {
        sections,
        showSectionTitles = false,
        filterStates,
        onUpdateFilterState,
        onUpdateFacetFilterState,
        onAlphabeticalGrouping,
    } = props;

    const isEnumFilter = (filter: FilterConfigModalItem): boolean => {
        if (filter.type !== 'default') return false;
        const state = resolveSectionFilterState(filter, filterStates);
        if (!state || isLocationFilterState(state)) return false;
        return (
            isSingleValueFilterState(state) ||
            isMultipleValuesFilterState(state)
        );
    };

    return (
        <div className="flex flex-col pb-6">
            {sections.map(({ section, isFacet }) => {
                const update = isFacet
                    ? onUpdateFacetFilterState
                    : onUpdateFilterState;
                const enumFilters = section.filters.filter(isEnumFilter);
                const otherFilters = section.filters.filter(
                    (filter) => !isEnumFilter(filter),
                );

                return (
                    <section
                        key={`drill-in-${section.title}`}
                        aria-label={section.title}
                    >
                        {showSectionTitles && (
                            <div className="flex min-h-11 items-center text-sm font-medium">
                                {section.title}
                            </div>
                        )}
                        {enumFilters.length > 0 && (
                            <RefinePillGroup
                                filters={enumFilters}
                                filterStates={filterStates}
                                onUpdateFilterState={update}
                            />
                        )}
                        {otherFilters.map((filterConfigItem) => {
                            const state = resolveSectionFilterState(
                                filterConfigItem,
                                filterStates,
                            );

                            // Colours get the labelled grid — their names are
                            // the taxonomy values, and swatches alone cannot
                            // distinguish Gold from Bronze from Brown.
                            if (
                                filterConfigItem.type === 'color' &&
                                state &&
                                isMultipleValuesFilterState(state)
                            ) {
                                return (
                                    <RefineColorGrid
                                        key={filterConfigItem.filterKey}
                                        filterConfigItem={filterConfigItem}
                                        filterState={state}
                                        onUpdateFilterState={update}
                                    />
                                );
                            }

                            return (
                                <FiltersModalElement
                                    key={filterConfigItem.filterKey}
                                    displayMode={DisplayMode.Mobile}
                                    filterConfigItem={filterConfigItem}
                                    filterState={state}
                                    onUpdateFilterState={update}
                                    onAlphabeticalGrouping={
                                        onAlphabeticalGrouping
                                    }
                                    // The chips row rides into sub-views, so a
                                    // searchable list's own selected chips
                                    // would triple up with it and the checked
                                    // rows below.
                                    hideSelectedListChips
                                />
                            );
                        })}
                    </section>
                );
            })}
        </div>
    );
};
